import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  auditLogKeys,
  contactKeys,
  creditMemoKeys,
  invoiceKeys,
  paymentKeys,
} from '@/lib/queryKeys';
import { useApiMutation } from '@/hooks/useApiMutation';
import { invalidateJobPaymentQueries } from '@/hooks/jobs/useJobs';
import { apiPost, operationsClient } from '@/lib/api/client';
import { billingV2Post } from '@/lib/api/clients/billing-v2';
import { billingV2Endpoints } from '@/lib/api/endpoints';
import { requestReference, type ApiError } from '@/lib/api/errors';
import { type PaymentRecord } from './usePayments';

export interface RefundPaymentInput {
  id: string;
  invoiceId?: string;
  contactId?: string | null;
  /** The job the payment was taken on, so its Billing tab refreshes too. */
  jobId?: string | null;
  reason?: string;
  amount?: number;
  gatewayProvider?: string | null;
  refundedAmount?: number | null;
  idempotencyKey: string;
  /**
   * When true, issue reusable customer credit without moving cash or changing
   * the original payment/invoice settlement. Never touches the gateway.
   */
  refundAsCredit?: boolean;
}

/** Nest's placeholder body for an unhandled 500 — it tells the operator nothing. */
const OPAQUE_SERVER_MESSAGES = new Set(['internal server error', 'error']);

/**
 * The server's own sentence for this refusal, when it wrote one.
 *
 * Every refund refusal the API classifies carries operator-grade copy in the
 * response body: a 402 names the decline, a 409 says "This charge has not
 * settled yet, so it cannot be partially refunded. Refund it in full (which
 * voids the charge) or retry after settlement.", a 503 says the reversal is
 * pending verification and must not be retried. Only a genuinely unhandled
 * failure arrives with nothing but a placeholder.
 */
function serverMessage(error: ApiError): string | undefined {
  const body = error.data as { message?: unknown } | null | undefined;
  const raw = body?.message;
  const text =
    typeof raw === 'string'
      ? raw
      : Array.isArray(raw)
        ? raw.filter((entry): entry is string => typeof entry === 'string').join('\n')
        : undefined;
  const trimmed = text?.trim();
  if (!trimmed) return undefined;
  return OPAQUE_SERVER_MESSAGES.has(trimmed.toLowerCase()) ? undefined : trimmed;
}

/**
 * What the operator is told when a refund is refused.
 *
 * This used to be five hard-coded sentences chosen by substring-matching the
 * error text, three of which named **Stripe** — on a Payabli tenant they were
 * simply wrong — and none of which could match the real refusals the API
 * sends. A partial refund of an unsettled Payabli charge is a correct 409
 * carrying "Refund it in full (which voids the charge) or retry after
 * settlement"; the operator saw "Refund could not be processed. Nothing was
 * changed locally. Try again later." That is the whole of QA's "Refund doesn't
 * work giving error" (audit F-C5-5 / Q2 item 6a).
 *
 * "Nothing was changed locally" is also not ours to promise: a refund whose
 * outcome could not be verified leaves a pending reservation behind by design.
 *
 * So: say what the server said, verbatim, whenever it said anything. Fall back
 * to a generic line only for a failure that carries no server copy at all —
 * and quote the request id there, because that failure is the one support has
 * to look up in the logs.
 *
 * Neither fallback may claim the refund did not happen. `status === 0` is
 * "no response reached this browser", which axios also produces for a timeout
 * or a dropped response on a request that WAS sent and may be running still.
 * Told "nothing was submitted", an operator reopens the dialog — which mints a
 * fresh idempotency key — and sends a second partial refund on top of the
 * first. That is the exact retry that eats refundable capacity, so the copy has
 * to send them to look at the payment instead.
 */
export function refundFailureMessage(error: ApiError): string {
  const fromServer = serverMessage(error);
  if (fromServer) return fromServer;

  const reference = requestReference(error);
  const generic =
    error.status === 0
      ? 'No response came back for this refund, so it may or may not have gone through. Check the payment before trying again.'
      : 'Refund could not be processed. Check the payment before retrying — the refund may still be pending with the processor.';
  return reference ? `${generic} (${reference})` : generic;
}

export function useRefundPayment() {
  const queryClient = useQueryClient();
  return useApiMutation<PaymentRecord, RefundPaymentInput>({
    mutationFn: async ({ id, reason, amount, gatewayProvider, refundAsCredit, idempotencyKey }) => {
      const idempotencyHeaders = { 'Idempotency-Key': idempotencyKey };
      // Issuing customer credit never touches the gateway or changes the
      // original payment/invoice settlement.
      if (refundAsCredit) {
        return apiPost<PaymentRecord, { reason?: string; amount?: number; refundAsCredit: true }>(
          operationsClient,
          `/payments/${id}/refund`,
          {
            ...(reason ? { reason } : {}),
            ...(amount !== undefined ? { amount } : {}),
            refundAsCredit: true,
          },
          { headers: idempotencyHeaders },
        );
      }

      if (gatewayProvider) {
        const response = await apiPost<
          { payment: PaymentRecord },
          { reason?: string; amount?: number; idempotencyKey: string }
        >(
          operationsClient,
          `/payments/${id}/refund/gateway`,
          {
            ...(reason ? { reason } : {}),
            ...(amount !== undefined ? { amount } : {}),
            idempotencyKey,
          },
          { headers: idempotencyHeaders },
        );
        return response.payment;
      }

      return billingV2Post<PaymentRecord, { reason?: string; amount?: number }>(
        billingV2Endpoints.paymentRefund(id),
        {
          ...(reason ? { reason } : {}),
          ...(amount !== undefined ? { amount } : {}),
        },
        { headers: idempotencyHeaders },
      );
    },
    onSuccess: (_updated, variables) => {
      // A processor refund can still be settling when this answers, so say it
      // was sent rather than that the money is back.
      toast.success(
        variables.refundAsCredit
          ? 'Customer credit issued'
          : variables.gatewayProvider
            ? 'Refund sent to the payment processor'
            : 'Refund recorded',
      );
      void queryClient.invalidateQueries({ queryKey: paymentKeys.all() });
      void queryClient.invalidateQueries({ queryKey: invoiceKeys.all() });
      // Both cash refunds and issued credits can create a linked credit memo.
      void queryClient.invalidateQueries({ queryKey: creditMemoKeys.all() });
      void queryClient.invalidateQueries({
        queryKey: auditLogKeys.byEntity('payment', variables.id),
      });
      // Issuing customer credit changes the contact's usable-credit balance;
      // cash refunds also change its financial activity. Refresh both views.
      if (variables.contactId) {
        void queryClient.invalidateQueries({
          queryKey: contactKeys.summary(variables.contactId),
        });
        void queryClient.invalidateQueries({
          queryKey: contactKeys.paymentsBase(variables.contactId),
        });
        void queryClient.invalidateQueries({
          queryKey: contactKeys.financialActivityBase(variables.contactId),
        });
      }
      if (variables.jobId) invalidateJobPaymentQueries(queryClient, variables.jobId);
      if (variables.invoiceId) {
        void queryClient.invalidateQueries({
          queryKey: invoiceKeys.detail(variables.invoiceId),
        });
        void queryClient.invalidateQueries({
          queryKey: [...invoiceKeys.detail(variables.invoiceId), 'payments'],
        });
      }
    },
    onError: (error) => {
      toast.error(refundFailureMessage(error));
    },
    showErrorToast: false,
  });
}
